import { useMemo, useState } from "react";
import { MessageCircle, PhoneCall, Send, Sparkles, X } from "lucide-react";
import { API_BASE_URL } from "@/apiConfig";

const DEFAULT_WHATSAPP_PHONE = "817036670926";
const WHATSAPP_PHONE = (
  process.env.REACT_APP_PUBLIC_BOOKING_WHATSAPP_PHONE ||
  DEFAULT_WHATSAPP_PHONE
).replace(/\D/g, "");

const languageOptions = [
  { id: "en", label: "English" },
  { id: "ja", label: "日本語" },
  { id: "zh", label: "中文" },
];

const copy = {
  en: {
    title: "AI Concierge",
    subtitle: "Ask about booking, payment, pickup, flight details, or cancellation.",
    greeting: "Hello, I am Shonan International's AI Concierge. How can I help with your ride today?",
    placeholder: "Type your question...",
    send: "Send",
    fallback: "Please add a little more detail and I will try again. For urgent service issues, WhatsApp is available.",
    whatsapp: "Contact on WhatsApp",
    quickTitle: "Quick questions",
    fallbackLead: "I could not verify this automatically yet.",
    thinking: "Checking with AI Concierge...",
  },
  ja: {
    title: "AI Concierge",
    subtitle: "予約、支払い、お迎え、フライト、キャンセルについて質問できます。",
    greeting: "こんにちは。Shonan International の AI Concierge です。本日のご移動についてお手伝いします。",
    placeholder: "質問を入力してください...",
    send: "送信",
    fallback: "内容を少し詳しく入力してください。緊急のサービス問題のみ、WhatsAppでも連絡できます。",
    whatsapp: "WhatsAppで連絡",
    quickTitle: "よくある質問",
    fallbackLead: "ただいま自動で確認できませんでした。",
    thinking: "AI Concierge が確認しています...",
  },
  zh: {
    title: "AI Concierge",
    subtitle: "可以咨询预约、付款、接送、航班、取消等问题。",
    greeting: "您好，我是 Shonan International 的 AI Concierge。请问今天需要什么帮助？",
    placeholder: "输入您的问题...",
    send: "发送",
    fallback: "您可以补充一点信息，我会继续尝试判断。只有紧急服务问题才建议使用 WhatsApp 联系工作人员。",
    whatsapp: "联系 WhatsApp",
    quickTitle: "常见问题",
    fallbackLead: "我暂时无法自动确认这个问题。",
    thinking: "AI Concierge 正在确认...",
  },
};

const quickQuestions = {
  en: [
    "How do I pay?",
    "What happens within 48 hours?",
    "How much is a child seat?",
    "Can I change my booking?",
  ],
  ja: [
    "支払い方法は？",
    "48時間以内の予約は？",
    "チャイルドシート料金は？",
    "予約変更はできますか？",
  ],
  zh: [
    "怎么付款？",
    "48小时内用车怎么办？",
    "儿童座椅怎么收费？",
    "可以修改预约吗？",
  ],
};

const answerRules = [
  {
    keys: [
      "highway toll",
      "highway tolls",
      "highway fee",
      "toll",
      "tolls",
      "高速费",
      "高速費",
      "高速料金",
      "高速道路料金",
      "有料道路",
    ],
    answers: {
      en: "Yes. For private charter and transfer bookings, quoted prices include vehicle, driver, fuel, highway tolls, and tax. Parking fees are separate from highway tolls and are included only when clearly confirmed.",
      ja: "はい。貸切チャーター・送迎のお見積り料金には、車両、ドライバー、燃料代、高速道路料金、税金が含まれます。駐車料金は高速道路料金とは別で、明確に確認された場合のみ含まれます。",
      zh: "包括。包车和接送的报价包含车辆、司机、燃油、高速费和税费。停车费和高速费不同，只有报价或工作人员明确说明时才包含。",
    },
  },
  {
    keys: ["charter one day", "full day charter", "how many hours", "包车一天", "包車一天", "几个小时", "幾個小時", "貸切一日", "何時間"],
    answers: {
      en: "A standard full-day private charter is usually 10 hours. We may also offer 8-hour plans depending on the itinerary. Overtime is charged by vehicle type if the service exceeds the included hours.",
      ja: "1日の貸切チャーターは通常10時間です。行程により8時間プランも選択できます。含まれる時間を超える場合は、車種ごとの延長料金が発生します。",
      zh: "包车一天通常是10小时，也可以根据行程选择8小时套餐。超过包含时间后，会按车型收取超时费。",
    },
  },
  {
    keys: ["fuji 5th", "5th station", "富士山五合目", "富士山5合目", "五合目"],
    answers: {
      en: "Fuji 5th Station can usually be arranged depending on season, road conditions, and weather. The additional fee is generally JPY 10,000.",
      ja: "富士山五合目は、季節・道路状況・天候により手配可能です。追加料金は通常10,000円です。",
      zh: "富士山五合目通常可以根据季节、道路状况和天气确认安排。追加费用一般为 ¥10,000。",
    },
  },
  {
    keys: ["cancellation policy", "before booking cancellation", "not booked", "没有预定", "還沒預定", "还没有预定", "未予約", "予約前"],
    answers: {
      en: "Before booking: cancellations are generally free until the payment/confirmation process is completed. After confirmation, cancellations within 48 hours before service start are generally non-refundable.",
      ja: "予約前の場合、決済・予約確定前は通常キャンセル料はかかりません。予約確定後は、ご利用開始48時間以内のキャンセルは原則返金不可です。",
      zh: "如果还没有完成预约和付款，一般不会产生取消费用。预约确认后，服务开始前48小时内取消原则上不可退款。",
    },
  },
  {
    keys: ["payment", "pay", "square", "checkout", "付款", "支付", "支払い", "決済"],
    answers: {
      en: "For online guest bookings, we create a secure Square payment link after the booking details are confirmed. You can pay from the link, and the booking is confirmed automatically after payment is completed.",
      ja: "オンライン予約では、内容確認後にSquareの安全な決済リンクを発行します。リンクからお支払いいただくと、決済完了後に予約が自動で確定します。",
      zh: "客人在线预约会在预约内容确认后生成 Square 安全付款链接。您通过链接付款后，系统会自动确认预约并发送确认邮件。",
    },
  },
  {
    keys: ["48 hours", "48 hour", "urgent", "today", "tomorrow", "48小时", "48時間", "急ぎ", "今天", "明天"],
    answers: {
      en: "Bookings within 48 hours require staff confirmation first. After we confirm availability, we will send you the payment link.",
      ja: "ご利用まで48時間以内の予約は、先にスタッフの確認が必要です。手配可能と確認でき次第、決済リンクをお送りします。",
      zh: "48小时以内的预约需要先由工作人员确认是否可以安排。确认后，系统会发送付款链接给您。",
    },
  },
  {
    keys: ["child seat", "baby seat", "child", "seat", "儿童座椅", "安全座椅", "チャイルドシート", "子供"],
    answers: {
      en: "The first child seat is free. From the second child seat, the price is ¥2,000 per seat and will be added to the final quote.",
      ja: "チャイルドシートは1台目無料です。2台目からは1台につき2,000円が最終料金に追加されます。",
      zh: "第一个儿童座椅免费，从第二个开始每个 ¥2,000，会加入最终报价。",
    },
  },
  {
    keys: ["cancel", "refund", "change", "modify", "取消", "退款", "修改", "変更", "キャンセル", "返金"],
    answers: {
      en: "Changes are possible when vehicle availability allows. Cancellations within 48 hours before service start are generally non-refundable, so please contact us early if your plan changes.",
      ja: "車両手配状況により変更可能です。ご利用開始48時間以内のキャンセルは原則返金不可のため、変更がある場合はお早めにご連絡ください。",
      zh: "如车辆安排允许，可以修改预约。服务开始前48小时内原则上不可取消、不可退款，如行程有变请尽早联系我们。",
    },
  },
  {
    keys: ["flight", "airport", "arrival", "departure", "terminal", "航班", "机场", "接机", "送机", "空港", "便名"],
    answers: {
      en: "For airport pickup or drop-off, please enter your flight number. We will use it to confirm the airport and flight time when available. If the airline data is incomplete, our staff will double-check it manually.",
      ja: "空港送迎では便名をご入力ください。取得できる場合は空港と時刻を確認します。航空会社データが不足している場合はスタッフが手動で確認します。",
      zh: "接送机请填写航班号。系统会尽量自动确认机场和航班时间；如果航班数据不完整，工作人员会人工确认。",
    },
  },
  {
    keys: ["luggage", "suitcase", "golf", "ski", "行李", "箱子", "高尔夫", "滑雪", "荷物", "スーツケース"],
    answers: {
      en: "Please enter the number of passengers and luggage accurately. For special luggage such as golf bags, ski boards, strollers, or oversized items, add a note before final confirmation.",
      ja: "人数と荷物数は正確にご入力ください。ゴルフバッグ、スキー板、ベビーカー、大型荷物などは最終確認前に備考へご記入ください。",
      zh: "请准确填写人数和行李数量。高尔夫包、滑雪板、婴儿车、大件行李等特殊行李，请在最终确认前写在备注里。",
    },
  },
  {
    keys: ["confirmation code", "check booking", "booking status", "确认码", "查询预约", "预约状态", "確認コード", "予約確認"],
    answers: {
      en: "After submitting a booking, please save your confirmation code. You can check your booking summary with the confirmation code and passenger full name.",
      ja: "予約送信後は確認コードを保存してください。確認コードと乗客の氏名で予約内容を確認できます。",
      zh: "提交预约后请保存确认码。您可以用确认码和乘客全名查询预约摘要。",
    },
  },
  {
    keys: ["driver", "chat", "communication", "司机", "聊天室", "联系司机", "運転手", "チャット"],
    answers: {
      en: "After the booking is confirmed, the trip communication center can be used for guest, driver, and staff messages when the service is enabled for the booking.",
      ja: "予約確定後、対象の予約でチャット機能が有効な場合は、お客様・運転手・スタッフがTrip Communication Centerで連絡できます。",
      zh: "预约确认后，如果该预约启用了聊天室功能，客户、司机、工作人员可以在行程沟通中心内联系。",
    },
  },
];

function findAnswer(question, lang) {
  const normalized = question.trim().toLowerCase();
  if (!normalized) return null;

  const match = answerRules.find((rule) =>
    rule.keys.some((keyword) => normalized.includes(keyword.toLowerCase()))
  );

  return match?.answers?.[lang] || null;
}

const whatsAppEscalationKeywords = [
  "urgent",
  "emergency",
  "complaint",
  "refund",
  "accident",
  "late",
  "driver late",
  "cannot find",
  "can't find",
  "paid but",
  "not confirmed",
  "flight cancelled",
  "flight canceled",
  "紧急",
  "投诉",
  "退款",
  "事故",
  "迟到",
  "晚到",
  "找不到司机",
  "找不到",
  "已付款",
  "没有确认",
  "未确认",
  "航班取消",
  "航班改签",
  "緊急",
  "苦情",
  "返金",
  "事故",
  "遅刻",
  "見つからない",
  "支払い済み",
  "未確認",
  "欠航",
  "便変更",
];

function shouldOfferWhatsApp(question) {
  const normalized = question.trim().toLowerCase();
  if (!normalized) return false;
  return whatsAppEscalationKeywords.some((keyword) => normalized.includes(keyword.toLowerCase()));
}

function getCurrentPageUrl() {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

async function askAiConcierge(question, lang) {
  const response = await fetch(`${API_BASE_URL}/public/customer-support/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      language: lang,
      page_url: getCurrentPageUrl(),
    }),
  });

  if (!response.ok) return null;
  const data = await response.json();
  return data?.success && data.answer ? data.answer : null;
}

export function buildWhatsAppUrl(question = "", lang = "en") {
  const pageUrl = getCurrentPageUrl();
  const text = [
    "Hello Shonan International, I need help with my booking.",
    question ? `Question: ${question}` : "",
    lang ? `Language: ${lang}` : "",
    pageUrl ? `Page: ${pageUrl}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const base = WHATSAPP_PHONE ? `https://wa.me/${WHATSAPP_PHONE}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(text)}`;
}

export default function CustomerSupportWidget() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);

  const activeCopy = copy[lang];
  const currentQuestion = useMemo(() => {
    const lastUserMessage = [...messages].reverse().find((message) => message.role === "user");
    return lastUserMessage?.text || input;
  }, [input, messages]);

  const submitQuestion = async (questionText = input) => {
    const question = questionText.trim();
    if (!question || sending) return;

    setSending(true);
    setMessages((current) => [
      ...current,
      { role: "user", text: question },
      { role: "assistant", text: activeCopy.thinking, pending: true },
    ]);
    setInput("");

    const localAnswer = findAnswer(question, lang);
    let aiAnswer = null;
    if (!localAnswer) {
      try {
        aiAnswer = await askAiConcierge(question, lang);
      } catch (error) {
        aiAnswer = null;
      }
    }

    const finalAnswer = localAnswer || aiAnswer || `${activeCopy.fallbackLead} ${activeCopy.fallback}`;
    setMessages((current) => [
      ...current.filter((message) => !message.pending),
      {
        role: "assistant",
        text: finalAnswer,
        needsWhatsApp: !localAnswer && !aiAnswer && shouldOfferWhatsApp(question),
      },
    ]);
    setSending(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] font-sans sm:bottom-6 sm:right-6">
      {open && (
        <section className="mb-3 w-[calc(100vw-2rem)] max-w-[390px] overflow-hidden rounded-[22px] border border-[#d7b967]/45 bg-[#070707]/95 text-white shadow-2xl shadow-black/45 backdrop-blur-xl">
          <div className="border-b border-white/10 bg-gradient-to-br from-[#111] via-[#090909] to-[#1b1507] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e8c76d] text-black shadow-lg shadow-[#e8c76d]/20">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="text-sm font-semibold tracking-[0.18em] text-[#e8c76d]">
                    {activeCopy.title}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-white/68">{activeCopy.subtitle}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-white/55 transition hover:bg-white/10 hover:text-white"
                aria-label="Close AI Concierge"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-3 rounded-full border border-white/10 bg-black/35 p-1">
              {languageOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setLang(option.id)}
                  className={`rounded-full px-2 py-1.5 text-[11px] font-semibold transition ${
                    lang === option.id
                      ? "bg-[#e8c76d] text-black"
                      : "text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[50vh] space-y-3 overflow-y-auto p-4">
            <div className="mr-8 rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.07] p-3 text-sm leading-relaxed text-white/82">
              {activeCopy.greeting}
            </div>

            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[84%] rounded-2xl p-3 text-sm leading-relaxed ${
                    message.role === "user"
                      ? "rounded-tr-sm bg-[#e8c76d] text-black"
                      : "rounded-tl-sm border border-white/10 bg-white/[0.07] text-white/82"
                  }`}
                >
                  <p>{message.text}</p>
                  {message.needsWhatsApp && (
                    <a
                      href={buildWhatsAppUrl(currentQuestion, lang)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#e8c76d] px-3 py-2 text-xs font-semibold text-black transition hover:bg-[#f2d784]"
                    >
                      <PhoneCall size={14} />
                      {activeCopy.whatsapp}
                    </a>
                  )}
                </div>
              </div>
            ))}

            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#e8c76d]/80">
                {activeCopy.quickTitle}
              </div>
              <div className="flex flex-wrap gap-2">
                {quickQuestions[lang].map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => submitQuestion(question)}
                    className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 text-xs text-white/76 transition hover:border-[#e8c76d]/55 hover:bg-[#e8c76d]/10 hover:text-white"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 bg-black/35 p-3">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") submitQuestion();
                }}
                placeholder={activeCopy.placeholder}
                className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.08] px-4 py-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#e8c76d]/70"
              />
              <button
                type="button"
                onClick={() => submitQuestion()}
                disabled={sending}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e8c76d] text-black transition hover:bg-[#f2d784] disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={activeCopy.send}
              >
                <Send size={17} />
              </button>
            </div>
          </div>
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-10 items-center justify-center gap-2 rounded-full border border-[#e8c76d]/55 bg-[#080808] p-0 text-sm font-semibold text-[#f0d37a] shadow-2xl shadow-black/45 transition hover:-translate-y-0.5 hover:bg-[#14100a] sm:h-auto sm:w-auto sm:px-4 sm:py-3"
        aria-label="Open AI Concierge"
      >
        <MessageCircle size={17} />
        <span className="hidden sm:inline">AI Concierge</span>
      </button>
    </div>
  );
}
